<template>
  <div
    v-if="modelValue"
    class="fixed inset-0 z-50 overflow-y-auto"
    aria-labelledby="modal-title"
    role="dialog"
    aria-modal="true"
  >
    <div
      class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
    >
      <!-- Background overlay -->
      <div
        class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        aria-hidden="true"
        @click="$emit('update:modelValue', false)"
      ></div>

      <!-- Modal panel -->
      <div
        class="inline-block align-bottom rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
      >
        <form @submit.prevent="handleSubmit">
          <div class="px-4 pt-5 pb-4 sm:p-6 sm:pb-4 bg-white dark:bg-gray-800">
            <div class="sm:flex sm:items-start">
              <div class="w-full">
                <h3 class="text-lg leading-6 font-medium mb-4 text-gray-900 dark:text-gray-100">
                  {{ editMode ? 'Edit Request Filter' : 'Create Request Filter' }}
                </h3>

                <!-- Path Input -->
                <div class="mb-4">
                  <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
                    >Path *</label
                  >
                  <input
                    v-model="formData.path"
                    type="text"
                    class="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
                    placeholder="/api/v1/example"
                    required
                  />
                </div>

                <!-- Version Input -->
                <div class="mb-4">
                  <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
                    >Version *</label
                  >
                  <input
                    v-model="formData.version"
                    type="text"
                    class="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
                    placeholder="v1"
                    required
                  />
                </div>

                <!-- Filter Sections -->
                <div class="space-y-4">
                  <!-- Body Filters -->
                  <div>
                    <div class="flex items-center justify-between mb-1">
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >Body Filters</label
                      >
                      <button
                        type="button"
                        class="text-sm text-blue-500 hover:text-blue-400"
                        @click="toggleSection('body')"
                      >
                        {{ showSections.body ? 'Hide' : 'Show' }}
                      </button>
                    </div>
                    <div
                      v-if="showSections.body"
                      class="border rounded-md p-3 border-gray-300 dark:border-gray-600"
                    >
                      <textarea
                        v-model="formData.filter.body"
                        class="w-full h-32 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 font-mono text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
                        placeholder='{ "key": "value" }'
                      ></textarea>
                    </div>
                  </div>

                  <!-- Query Filters -->
                  <div>
                    <div class="flex items-center justify-between mb-1">
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >Query Filters</label
                      >
                      <button
                        type="button"
                        class="text-sm text-blue-500 hover:text-blue-400"
                        @click="toggleSection('query')"
                      >
                        {{ showSections.query ? 'Hide' : 'Show' }}
                      </button>
                    </div>
                    <div
                      v-if="showSections.query"
                      class="border rounded-md p-3 border-gray-300 dark:border-gray-600"
                    >
                      <textarea
                        v-model="formData.filter.query"
                        class="w-full h-32 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 font-mono text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
                        placeholder='{ "param": "value" }'
                      ></textarea>
                    </div>
                  </div>

                  <!-- Params Filters -->
                  <div>
                    <div class="flex items-center justify-between mb-1">
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >Params Filters</label
                      >
                      <button
                        type="button"
                        class="text-sm text-blue-500 hover:text-blue-400"
                        @click="toggleSection('params')"
                      >
                        {{ showSections.params ? 'Hide' : 'Show' }}
                      </button>
                    </div>
                    <div
                      v-if="showSections.params"
                      class="border rounded-md p-3 border-gray-300 dark:border-gray-600"
                    >
                      <textarea
                        v-model="formData.filter.params"
                        class="w-full h-32 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 font-mono text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
                        placeholder='{ "id": ":id" }'
                      ></textarea>
                    </div>
                  </div>

                  <!-- Headers Filters -->
                  <div>
                    <div class="flex items-center justify-between mb-1">
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >Headers Filters</label
                      >
                      <button
                        type="button"
                        class="text-sm text-blue-500 hover:text-blue-400"
                        @click="toggleSection('headers')"
                      >
                        {{ showSections.headers ? 'Hide' : 'Show' }}
                      </button>
                    </div>
                    <div
                      v-if="showSections.headers"
                      class="border rounded-md p-3 border-gray-300 dark:border-gray-600"
                    >
                      <textarea
                        v-model="formData.filter.headers"
                        class="w-full h-32 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 font-mono text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
                        placeholder='{ "Authorization": "Bearer token" }'
                      ></textarea>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse bg-gray-50 dark:bg-gray-700">
            <button
              type="submit"
              class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2.5 bg-blue-500 text-base font-medium text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-400 sm:w-auto sm:text-sm"
              :disabled="isSubmitting"
            >
              {{
                isSubmitting
                  ? editMode
                    ? 'Updating...'
                    : 'Creating...'
                  : editMode
                    ? 'Update Filter'
                    : 'Create Filter'
              }}
            </button>
            <button
              type="button"
              class="mt-3 w-full inline-flex justify-center rounded-md border shadow-sm px-4 py-2.5 text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:mr-3 sm:w-auto sm:text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600"
              @click="$emit('update:modelValue', false)"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
  <Toast
    v-model:show="toast.show"
    :message="toast.message"
    :type="toast.type"
    :details="toast.details"
    :duration="5000"
  />
</template>

<script setup lang="ts">
import { ref, reactive, watch } from 'vue'
import { istioMeshApiClient } from '../../../services/api/istioMeshApiClient'
import Toast from '../../base/common/Toast.vue'
import type { RequestFilter } from '../../../types/request-filter'

interface FilterData {
  body: Record<string, unknown>
  query: Record<string, unknown>
  params: Record<string, unknown>
  headers: Record<string, unknown>
  [key: string]: Record<string, unknown> | unknown
}

interface ToastState {
  show: boolean
  message: string
  type: 'error' | 'success'
  details?: string[]
}

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'created'): void
  (e: 'updated'): void
}>()

const props = defineProps<{
  modelValue: boolean
  editMode?: boolean
  filterData?: RequestFilter | null
}>()

const isSubmitting = ref(false)
const showSections = reactive({
  body: false,
  query: false,
  params: false,
  headers: false
})

const formData = reactive({
  path: '',
  version: '',
  filter: {
    body: '',
    query: '',
    params: '',
    headers: ''
  }
})

const toast = reactive<ToastState>({
  show: false,
  message: '',
  type: 'success',
  details: []
})

// Reset form when modal closes
watch(
  () => props.modelValue,
  (newValue) => {
    if (!newValue) {
      // Only reset if not in edit mode or if edit mode is finished
      if (!props.editMode) {
        Object.assign(formData, {
          path: '',
          version: '',
          filter: {
            body: '',
            query: '',
            params: '',
            headers: ''
          }
        })
      }
    }
  }
)

// Initialize form data when filterData changes
watch(
  () => props.filterData,
  (newFilter) => {
    if (newFilter) {
      formData.path = newFilter.path
      formData.version = newFilter.version
      formData.filter = {
        body: newFilter.filter?.body ? JSON.stringify(newFilter.filter.body, null, 2) : '',
        query: newFilter.filter?.query ? JSON.stringify(newFilter.filter.query, null, 2) : '',
        params: newFilter.filter?.params ? JSON.stringify(newFilter.filter.params, null, 2) : '',
        headers: newFilter.filter?.headers ? JSON.stringify(newFilter.filter.headers, null, 2) : ''
      }
    }
  },
  { immediate: true }
)

const toggleSection = (section: keyof typeof showSections) => {
  showSections[section] = !showSections[section]
}

const validateJSON = (value: string, field: string): string[] => {
  // Skip validation if the field is empty or only contains whitespace
  if (!value || value.trim() === '') return []
  try {
    JSON.parse(value)
    return []
  } catch (e) {
    return [`Invalid JSON in ${field} filter`]
  }
}

const showError = (message: string, details?: string[]) => {
  toast.type = 'error'
  toast.message = message
  toast.details = details
  toast.show = true
}

const showSuccess = (message: string) => {
  toast.type = 'success'
  toast.message = message
  toast.details = []
  toast.show = true
}

const handleSubmit = async () => {
  try {
    isSubmitting.value = true

    // Validate JSON in all filter fields
    const errors: string[] = []
    if (formData.filter.body?.trim()) {
      errors.push(...validateJSON(formData.filter.body, 'body'))
    }
    if (formData.filter.query?.trim()) {
      errors.push(...validateJSON(formData.filter.query, 'query'))
    }
    if (formData.filter.params?.trim()) {
      errors.push(...validateJSON(formData.filter.params, 'params'))
    }
    if (formData.filter.headers?.trim()) {
      errors.push(...validateJSON(formData.filter.headers, 'headers'))
    }

    if (errors.length > 0) {
      showError('Please fix the following errors:', errors)
      return
    }

    // Parse JSON strings to objects
    const filterData = {
      path: formData.path,
      version: formData.version,
      filter: {
        body: formData.filter.body?.trim() ? JSON.parse(formData.filter.body) : undefined,
        query: formData.filter.query?.trim() ? JSON.parse(formData.filter.query) : undefined,
        params: formData.filter.params?.trim() ? JSON.parse(formData.filter.params) : undefined,
        headers: formData.filter.headers?.trim() ? JSON.parse(formData.filter.headers) : undefined
      }
    }

    // Remove undefined filter properties
    const filter = filterData.filter as FilterData
    Object.keys(filter).forEach((key) => {
      if (filter[key] === undefined) {
        delete filter[key]
      }
    })

    try {
      if (props.editMode && props.filterData?._id) {
        // Update existing filter
        await istioMeshApiClient.put(
          `/platform/istio/request-filter/${props.filterData._id}`,
          filterData
        )
        showSuccess('Request filter updated successfully')
        emit('updated')
      } else {
        // Create new filter
        await istioMeshApiClient.post('/platform/istio/request-filter', filterData)
        showSuccess('Request filter created successfully')
        emit('created')
      }

      emit('update:modelValue', false)

      // Reset form
      Object.assign(formData, {
        path: '',
        version: '',
        filter: {
          body: '',
          query: '',
          params: '',
          headers: ''
        }
      })
    } catch (error: unknown) {
      // Handle API-specific errors
      let errorMessage = props.editMode ? 'Failed to update filter' : 'Failed to create filter'
      if (error instanceof Error) {
        const message = error.message
        if (message.includes('duplicate key error')) {
          errorMessage = 'A filter with this path already exists'
        } else if (message.includes('Error in creating Istio Request Filter')) {
          errorMessage = message.split(': ').slice(1).join(': ').trim()
        } else {
          errorMessage = message
        }
      }
      showError(errorMessage)
    }
  } catch (error) {
    // Failed to process filter - show user-friendly error instead of console.error
    showError('Invalid input', ['Please make sure all filled JSON fields are properly formatted.'])
  } finally {
    isSubmitting.value = false
  }
}
</script>
